import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import MapSelector from '@/components/MapSelector';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Play, Pause, Square, Navigation, Download, Save, Trash2, 
  ArrowLeft, MapPin, Clock, Route, AlertCircle, Locate, Share2, MessageCircle, Mail, Check,
  Radio, Users, CloudOff, HardDrive, Wifi, WifiOff, StopCircle, Cloud, CloudLightning
} from 'lucide-react';
import { toast } from 'sonner';
import { useOfflineMap } from '@/hooks/useOfflineMap';
import { useOfflineLocationQueue } from '@/hooks/useOfflineLocationQueue';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Component to handle map centering on user position
function MapCenterOnUser({ position, shouldCenter }) {
  const map = useMap();
  
  useEffect(() => {
    if (position && shouldCenter) {
      map.setView(position, map.getZoom());
    }
  }, [position, shouldCenter, map]);
  
  return null;
}

// Component to fit bounds to track
function FitBoundsToTrack({ coordinates }) {
  const map = useMap();
  
  useEffect(() => {
    if (coordinates && coordinates.length > 0) {
      const bounds = L.latLngBounds(coordinates);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [coordinates, map]);
  
  return null;
}

export default function RouteTracker({ user }) {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState(null);
  const [eventTrackCoordinates, setEventTrackCoordinates] = useState([]);
  const [center, setCenter] = useState([40.4168, -3.7038]);
  
  // Available event tracks
  const [availableEventTracks, setAvailableEventTracks] = useState([]);
  const [selectedEventTrack, setSelectedEventTrack] = useState(null);
  
  // GPS tracking state
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [userPosition, setUserPosition] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedPath, setRecordedPath] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [distance, setDistance] = useState(0);
  const [followUser, setFollowUser] = useState(true);
  const [gpsError, setGpsError] = useState(null);
  
  // Live tracking state (visibility on shared map)
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [lastLocationSent, setLastLocationSent] = useState(null); // Debug: track last sent time
  const [locationSendError, setLocationSendError] = useState(null); // Debug: track errors
  
  // Saved tracks state
  const [savedTracks, setSavedTracks] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [lastSavedTrack, setLastSavedTrack] = useState(null);
  const [trackName, setTrackName] = useState('');
  const [savingTrack, setSavingTrack] = useState(false);
  const [selectedTrackToView, setSelectedTrackToView] = useState(null);
  
  // Refs
  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const trackingIntervalRef = useRef(null); // For periodic location updates
  const wakeLockRef = useRef(null); // For keeping screen awake

  // Offline map hook
  const {
    isSupported: offlineSupported,
    downloading: downloadingMap,
    cancelled: downloadCancelled,
    progress: downloadProgress,
    cacheInfo,
    error: offlineError,
    downloadTrackArea,
    clearCache,
    cancelDownload,
    estimateDownloadSize
  } = useOfflineMap();

  // Offline location queue hook
  const {
    isOnline,
    queueSize,
    isSyncing,
    pendingCount,
    syncError,
    addToQueue,
    syncQueue
  } = useOfflineLocationQueue(registrationId);

  // Fetch registration and event data
  useEffect(() => {
    fetchData();
    fetchSavedTracks();
    return () => {
      stopGPSWatch();
      if (timerRef.current) clearInterval(timerRef.current);
      if (trackingIntervalRef.current) clearInterval(trackingIntervalRef.current);
      // Release wake lock on unmount
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
      }
    };
  }, [registrationId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get registration with event data
      const regResponse = await axios.get(`${API}/registrations/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const reg = regResponse.data.find(r => r.id === registrationId);
      if (!reg) {
        toast.error('Inscripción no encontrada');
        navigate('/my-registrations');
        return;
      }
      
      setRegistration(reg);
      
      // Build list of available tracks
      const tracks = [];
      const event = reg.event;
      
      // Add main GPX file if exists
      if (event?.gpx_file) {
        tracks.push({ id: 'main', name: 'Track Principal', file: event.gpx_file, type: 'gpx_file' });
      }
      
      // Add numbered tracks (1-7)
      for (let i = 1; i <= 7; i++) {
        const trackFile = event?.[`track${i}_file`];
        const trackName = event?.[`track${i}_name`] || `Track ${i}`;
        if (trackFile) {
          tracks.push({ id: `track${i}`, name: trackName, file: trackFile, type: 'numbered' });
        }
      }
      
      setAvailableEventTracks(tracks);
      
      // Auto-select first track if available
      if (tracks.length > 0) {
        setSelectedEventTrack(tracks[0]);
        await loadTrackFromUrl(tracks[0].file, tracks[0].type);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackFromUrl = async (fileUrl, type) => {
    try {
      if (type === 'gpx_file' && registration?.event?.id) {
        // Try API endpoint for main GPX file
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/gpx/${registration.event.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.content) {
          parseGPX(response.data.content);
          return;
        }
      }
      
      // Direct fetch for Cloudinary URLs
      const gpxResponse = await fetch(fileUrl);
      const gpxText = await gpxResponse.text();
      parseGPX(gpxText);
    } catch (error) {
      console.error('Error loading track:', error);
      toast.error('Error al cargar el track');
    }
  };

  const handleSelectEventTrack = async (track) => {
    setSelectedEventTrack(track);
    setEventTrackCoordinates([]); // Clear current track
    await loadTrackFromUrl(track.file, track.type);
  };

  const loadEventTrack = async (event) => {
    try {
      // Try to get GPX content from main gpx_file first
      const token = localStorage.getItem('token');
      
      if (event.gpx_file) {
        const response = await axios.get(`${API}/gpx/${event.id}/preview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.content) {
          parseGPX(response.data.content);
          return;
        }
      }
      
      // Try track files (track1_file, track2_file, etc.)
      const trackFile = event.track1_file || event.track2_file || event.track3_file;
      if (trackFile) {
        try {
          const gpxResponse = await fetch(trackFile);
          const gpxText = await gpxResponse.text();
          parseGPX(gpxText);
          return;
        } catch (e) {
          console.error('Error loading track file:', e);
        }
      }
    } catch (error) {
      console.error('Error loading event track:', error);
      // Fallback: try track files directly
      const trackFile = event.track1_file || event.track2_file;
      if (trackFile) {
        try {
          const gpxResponse = await fetch(trackFile);
          const gpxText = await gpxResponse.text();
          parseGPX(gpxText);
        } catch (e) {
          console.error('Error loading direct track:', e);
        }
      }
    }
  };

  const parseGPX = (gpxString) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxString, 'text/xml');
    
    let trackPoints = xmlDoc.getElementsByTagName('trkpt');
    if (trackPoints.length === 0) trackPoints = xmlDoc.getElementsByTagName('rtept');
    if (trackPoints.length === 0) trackPoints = xmlDoc.getElementsByTagName('wpt');
    
    const coords = [];
    for (let i = 0; i < trackPoints.length; i++) {
      const lat = parseFloat(trackPoints[i].getAttribute('lat'));
      const lon = parseFloat(trackPoints[i].getAttribute('lon'));
      if (!isNaN(lat) && !isNaN(lon)) {
        coords.push([lat, lon]);
      }
    }
    
    if (coords.length > 0) {
      setEventTrackCoordinates(coords);
      setCenter(coords[0]);
    }
  };

  const fetchSavedTracks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/registrations/${registrationId}/recorded-tracks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedTracks(response.data || []);
    } catch (error) {
      console.error('Error fetching saved tracks:', error);
    }
  };

  // GPS Functions
  const startGPSWatch = () => {
    if (!navigator.geolocation) {
      setGpsError('Tu navegador no soporta geolocalización');
      toast.error('Geolocalización no disponible');
      return;
    }

    setGpsError(null);
    setGpsEnabled(true);
    
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, altitude, accuracy, speed, heading } = position.coords;
        const newPosition = [latitude, longitude];
        setUserPosition(newPosition);
        
        // Send live location to server
        sendLiveLocation(latitude, longitude, altitude, accuracy, speed, heading);
        
        if (isRecording && !isPaused) {
          const newPoint = {
            lat: latitude,
            lon: longitude,
            elevation: altitude || null,
            timestamp: new Date().toISOString()
          };
          
          setRecordedPath(prev => {
            const updated = [...prev, newPoint];
            // Calculate distance
            if (prev.length > 0) {
              const lastPoint = prev[prev.length - 1];
              const dist = calculateDistance(lastPoint.lat, lastPoint.lon, latitude, longitude);
              setDistance(d => d + dist);
            }
            return updated;
          });
        }
      },
      (error) => {
        console.error('GPS Error:', error);
        setGpsError(getGPSErrorMessage(error));
        setGpsEnabled(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 1000
      }
    );
  };

  // Send live location to server for Rally Master/Admin tracking (only if tracking is enabled)
  const sendLiveLocation = async (latitude, longitude, altitude, accuracy, speed, heading) => {
    if (!trackingEnabled) return; // Only send if tracking is enabled
    
    const locationData = {
      latitude,
      longitude,
      altitude,
      accuracy,
      speed,
      heading,
      timestamp: new Date().toISOString()
    };

    // Check if we're online
    if (!navigator.onLine) {
      // Offline: add to queue
      console.log('[Offline] Adding location to queue:', locationData);
      addToQueue(locationData);
      setLastLocationSent(`${new Date().toLocaleTimeString()} (cola)`);
      setLocationSendError(null);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      console.log('Sending live location:', { registrationId, latitude, longitude, trackingEnabled });
      const response = await axios.post(
        `${API}/live-location`,
        null,
        {
          params: {
            registration_id: registrationId,
            latitude,
            longitude,
            altitude,
            accuracy,
            speed,
            heading
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Live location sent successfully:', response.status);
      setLastLocationSent(new Date().toLocaleTimeString());
      setLocationSendError(null);
      
      // If we're back online and have queued items, sync them
      if (pendingCount > 0) {
        console.log('Syncing queued locations...');
        syncQueue();
      }
    } catch (error) {
      // Check if it's a network error
      if (!navigator.onLine || error.message === 'Network Error') {
        console.log('[Offline] Network error, adding to queue');
        addToQueue(locationData);
        setLastLocationSent(`${new Date().toLocaleTimeString()} (cola)`);
        setLocationSendError('Sin conexión - guardando en cola');
      } else {
        console.error('Error sending live location:', error.response?.data || error.message);
        const errorMsg = error.response?.data?.detail || error.message;
        setLocationSendError(errorMsg);
        toast.error('Error al enviar ubicación: ' + errorMsg);
      }
    }
  };

  // Delete live location when stopping GPS
  const deleteLiveLocation = async () => {
    try {
      const token = localStorage.getItem('token');
      const eventId = registration?.event?.id;
      if (eventId) {
        await axios.delete(`${API}/live-location/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.log('Error deleting live location:', error.message);
    }
  };

  const stopGPSWatch = () => {
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setGpsEnabled(false);
    
    // Also disable tracking when GPS is stopped
    if (trackingEnabled) {
      setTrackingEnabled(false);
      deleteLiveLocation(); // Remove from live map
    }
    
    setUserPosition(null);
  };

  const toggleGPS = () => {
    if (gpsEnabled) {
      stopGPSWatch();
      toast.info('GPS desactivado');
    } else {
      startGPSWatch();
      toast.success('GPS activado');
    }
  };

  // Toggle tracking visibility on shared map
  // Wake Lock functions to keep screen awake during tracking
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock activated - screen will stay on');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('Wake Lock released');
        });
        return true;
      } else {
        console.log('Wake Lock API not supported');
        return false;
      }
    } catch (err) {
      console.error('Wake Lock error:', err);
      return false;
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('Wake Lock released manually');
      } catch (err) {
        console.error('Error releasing Wake Lock:', err);
      }
    }
  };

  const toggleTracking = async () => {
    if (trackingEnabled) {
      // Disable tracking - remove from live map
      setTrackingEnabled(false);
      setLastLocationSent(null);
      setLocationSendError(null);
      // Clear the periodic interval
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
        trackingIntervalRef.current = null;
      }
      // Release wake lock
      await releaseWakeLock();
      await deleteLiveLocation();
      toast.info('Seguimiento desactivado - Ya no serás visible en el mapa compartido');
    } else {
      // Enable tracking - will start sending location
      if (!gpsEnabled) {
        // Need to enable GPS first
        startGPSWatch();
      }
      setTrackingEnabled(true);
      setLastLocationSent(null);
      setLocationSendError(null);
      
      // Request wake lock to keep screen on
      const wakeLockObtained = await requestWakeLock();
      if (wakeLockObtained) {
        toast.success('Seguimiento activado - Pantalla permanecerá encendida');
      } else {
        toast.success('Seguimiento activado - Mantén la pantalla encendida manualmente');
      }
      
      // Send current position immediately if available
      if (userPosition) {
        console.log('Sending initial position:', userPosition);
        await sendLiveLocationForced(userPosition[0], userPosition[1]);
      } else {
        console.log('No userPosition available yet, will send when GPS provides position');
        // Try to get position directly
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              console.log('Got immediate position:', latitude, longitude);
              await sendLiveLocationForced(latitude, longitude);
            },
            (error) => {
              console.error('Error getting immediate position:', error);
              setLocationSendError('No se pudo obtener la posición GPS');
            },
            { enableHighAccuracy: true, timeout: 10000 }
          );
        }
      }
      
      // Set up periodic location sending every 15 seconds
      trackingIntervalRef.current = setInterval(async () => {
        if (userPosition) {
          console.log('Periodic location send:', userPosition);
          await sendLiveLocationForced(userPosition[0], userPosition[1]);
        } else {
          console.log('Periodic send skipped - no position');
        }
      }, 15000);
    }
  };

  // Force send location (bypasses trackingEnabled check for initial send)
  const sendLiveLocationForced = async (latitude, longitude) => {
    const locationData = {
      latitude,
      longitude,
      timestamp: new Date().toISOString()
    };

    // Check if we're online
    if (!navigator.onLine) {
      console.log('[Offline] Adding forced location to queue:', locationData);
      addToQueue(locationData);
      setLastLocationSent(`${new Date().toLocaleTimeString()} (cola)`);
      setLocationSendError('Sin conexión - guardando en cola');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      console.log('Force sending live location:', { registrationId, latitude, longitude });
      const response = await axios.post(
        `${API}/live-location`,
        null,
        {
          params: {
            registration_id: registrationId,
            latitude,
            longitude
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Force live location sent successfully:', response.status);
      setLastLocationSent(new Date().toLocaleTimeString());
      setLocationSendError(null);
      
      // Sync any pending queue items
      if (pendingCount > 0) {
        syncQueue();
      }
    } catch (error) {
      if (!navigator.onLine || error.message === 'Network Error') {
        console.log('[Offline] Network error on forced send, adding to queue');
        addToQueue(locationData);
        setLastLocationSent(`${new Date().toLocaleTimeString()} (cola)`);
        setLocationSendError('Sin conexión - guardando en cola');
      } else {
        console.error('Error force sending live location:', error.response?.data || error.message);
        const errorMsg = error.response?.data?.detail || error.message;
        setLocationSendError(errorMsg);
        toast.error('Error al activar seguimiento: ' + errorMsg);
      }
    }
  };

  const getGPSErrorMessage = (error) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return 'Permiso de ubicación denegado. Por favor, habilita la ubicación en tu navegador.';
      case error.POSITION_UNAVAILABLE:
        return 'Ubicación no disponible. Verifica que el GPS esté activado.';
      case error.TIMEOUT:
        return 'Tiempo de espera agotado. Intenta de nuevo.';
      default:
        return 'Error al obtener ubicación.';
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Recording controls
  const handleStartRecording = () => {
    setRecordedPath([]);
    setDistance(0);
    setElapsedTime(0);
    setStartTime(new Date());
    setIsRecording(true);
    setIsPaused(false);
    
    // Start GPS if not already enabled
    if (!gpsEnabled) {
      startGPSWatch();
    }
    
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    toast.success('Grabación iniciada');
  };

  const handlePauseRecording = () => {
    setIsPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
    toast.info('Grabación pausada');
  };

  const handleResumeRecording = () => {
    setIsPaused(false);
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    toast.success('Grabación reanudada');
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    // Don't stop GPS - user might want to keep seeing their position
    if (timerRef.current) clearInterval(timerRef.current);
    
    if (recordedPath.length > 1) {
      setShowSaveDialog(true);
    } else {
      toast.warning('No hay suficientes puntos grabados');
    }
  };

  const handleDiscardRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    // Don't stop GPS - user might want to keep seeing their position
    if (timerRef.current) clearInterval(timerRef.current);
    setRecordedPath([]);
    setDistance(0);
    setElapsedTime(0);
    toast.info('Grabación descartada');
  };

  // Save track
  const handleSaveTrack = async () => {
    if (recordedPath.length < 2) {
      toast.error('No hay suficientes puntos para guardar');
      return;
    }
    
    setSavingTrack(true);
    try {
      const token = localStorage.getItem('token');
      const savedTrackName = trackName || `Track ${new Date().toLocaleDateString('es-ES')}`;
      const response = await axios.post(
        `${API}/registrations/${registrationId}/recorded-tracks`,
        {
          name: savedTrackName,
          coordinates: recordedPath,
          distance_km: distance,
          duration_seconds: elapsedTime,
          started_at: startTime?.toISOString(),
          ended_at: new Date().toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Track guardado correctamente');
      
      // Store the saved track info for sharing
      setLastSavedTrack({
        id: response.data.track?.id,
        name: savedTrackName,
        distance_km: distance,
        duration_seconds: elapsedTime
      });
      
      setShowSaveDialog(false);
      setShowShareDialog(true); // Show share options
      setRecordedPath([]);
      setDistance(0);
      setElapsedTime(0);
      setTrackName('');
      fetchSavedTracks();
    } catch (error) {
      console.error('Error saving track:', error);
      toast.error('Error al guardar el track');
    } finally {
      setSavingTrack(false);
    }
  };

  // Share track functions
  const getShareText = () => {
    if (!lastSavedTrack) return '';
    const eventName = registration?.event?.title || 'Evento';
    return `🏍️ ¡He completado "${lastSavedTrack.name}" en ${eventName}!\n📏 Distancia: ${lastSavedTrack.distance_km?.toFixed(2)} km\n⏱️ Tiempo: ${formatTime(lastSavedTrack.duration_seconds || 0)}`;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaTelegram = () => {
    const text = encodeURIComponent(getShareText());
    window.open(`https://t.me/share/url?text=${text}`, '_blank');
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Mi track: ${lastSavedTrack?.name}`);
    const body = encodeURIComponent(getShareText());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  // Delete track
  const handleDeleteTrack = async (trackId) => {
    if (!confirm('¿Estás seguro de eliminar este track?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/registrations/${registrationId}/recorded-tracks/${trackId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Track eliminado');
      fetchSavedTracks();
      if (selectedTrackToView?.id === trackId) {
        setSelectedTrackToView(null);
      }
    } catch (error) {
      toast.error('Error al eliminar el track');
    }
  };

  // Export track
  const handleExportTrack = async (trackId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/registrations/${registrationId}/recorded-tracks/${trackId}/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `track_${trackId}.gpx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Track exportado como GPX');
    } catch (error) {
      toast.error('Error al exportar el track');
    }
  };

  // Center map on user
  const handleCenterOnUser = () => {
    setFollowUser(true);
  };

  // Format time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando mapa...</p>
        </div>
      </div>
    );
  }

  const recordedPathForMap = recordedPath.map(p => [p.lat, p.lon]);
  const selectedTrackCoords = selectedTrackToView?.coordinates?.map(p => [p.lat, p.lon]) || [];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/my-registrations')}
              className="text-gray-300 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Volver
            </Button>
            <div>
              <h1 className="text-white font-semibold">{registration?.event?.title}</h1>
              <p className="text-gray-400 text-sm">Seguimiento de Ruta</p>
            </div>
          </div>
          
          {/* GPS Status - Toggle Button */}
          <div className="flex items-center gap-2">
            <Button
              onClick={toggleGPS}
              variant={gpsEnabled ? "default" : "outline"}
              size="sm"
              className={gpsEnabled 
                ? "bg-green-600 hover:bg-green-700 text-white" 
                : "border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white"
              }
              disabled={isRecording}
              data-testid="toggle-gps-btn"
            >
              <Navigation className="w-3 h-3 mr-1" />
              {gpsEnabled ? (userPosition ? 'GPS Activo' : 'Buscando...') : 'Encender GPS'}
            </Button>
            {isRecording && gpsEnabled && (
              <Badge className="bg-red-600 animate-pulse">
                REC
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)]">
        {/* Map */}
        <div className="flex-1 relative">
          {/* Map Style Selector */}
          <div className="absolute top-2 right-2 z-[1000]">
            <MapSelector compact />
          </div>
          
          <MapContainer
            center={center}
            zoom={14}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <DynamicTileLayer maxZoom={17} />
            
            {/* Event Track (magenta) */}
            {eventTrackCoordinates.length > 0 && (
              <>
                <Polyline
                  positions={eventTrackCoordinates}
                  color="#d946ef"
                  weight={4}
                  opacity={0.8}
                />
                <Marker position={eventTrackCoordinates[0]}>
                  <Popup>Inicio del recorrido</Popup>
                </Marker>
                <Marker position={eventTrackCoordinates[eventTrackCoordinates.length - 1]}>
                  <Popup>Fin del recorrido</Popup>
                </Marker>
              </>
            )}
            
            {/* Recorded Path (black) */}
            {recordedPathForMap.length > 1 && (
              <Polyline
                positions={recordedPathForMap}
                color="#000000"
                weight={4}
                opacity={0.9}
              />
            )}
            
            {/* Selected saved track (green) */}
            {selectedTrackCoords.length > 1 && (
              <Polyline
                positions={selectedTrackCoords}
                color="#22c55e"
                weight={4}
                opacity={0.9}
              />
            )}
            
            {/* User Position */}
            {userPosition && (
              <CircleMarker
                center={userPosition}
                radius={10}
                fillColor="#3b82f6"
                fillOpacity={1}
                color="#ffffff"
                weight={3}
              >
                <Popup>Tu ubicación actual</Popup>
              </CircleMarker>
            )}
            
            <MapCenterOnUser position={userPosition} shouldCenter={followUser && userPosition} />
            {eventTrackCoordinates.length > 0 && !userPosition && (
              <FitBoundsToTrack coordinates={eventTrackCoordinates} />
            )}
          </MapContainer>

          {/* Map Controls */}
          <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCenterOnUser}
              disabled={!userPosition}
              className="bg-white shadow-lg"
            >
              <Locate className="w-4 h-4" />
            </Button>
          </div>

          {/* GPS Error Message */}
          {gpsError && (
            <div className="absolute top-4 left-4 right-20 z-[1000]">
              <div className="bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                {gpsError}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 border-l border-gray-700 overflow-y-auto">
          {/* Event Tracks Selector */}
          {availableEventTracks.length > 0 && (
            <div className="p-4 border-b border-gray-700">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-fuchsia-500" />
                Tracks del Evento ({availableEventTracks.length})
              </h3>
              <div className="space-y-2">
                {availableEventTracks.map((track) => (
                  <button
                    key={track.id}
                    onClick={() => handleSelectEventTrack(track)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      selectedEventTrack?.id === track.id
                        ? 'bg-fuchsia-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium truncate">{track.name}</span>
                      {selectedEventTrack?.id === track.id && (
                        <Badge className="bg-fuchsia-700 text-xs">Activo</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {availableEventTracks.length > 1 && (
                <p className="text-xs text-gray-500 mt-2">
                  Selecciona el track que quieres seguir
                </p>
              )}
            </div>
          )}

          {/* Recording Stats */}
          {isRecording && (
            <div className="p-4 bg-blue-900 border-b border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <span className="text-blue-300 font-medium flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}></div>
                  {isPaused ? 'Pausado' : 'Grabando'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-mono text-white">{formatTime(elapsedTime)}</p>
                  <p className="text-xs text-gray-400">Tiempo</p>
                </div>
                <div>
                  <p className="text-2xl font-mono text-white">{distance.toFixed(2)}</p>
                  <p className="text-xs text-gray-400">km</p>
                </div>
              </div>
            </div>
          )}

          {/* Recording Controls */}
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Route className="w-4 h-4" />
              Control de Grabación
            </h3>
            
            {!isRecording ? (
              <Button
                onClick={handleStartRecording}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="start-recording-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Iniciar Grabación
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  {isPaused ? (
                    <Button
                      onClick={handleResumeRecording}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Reanudar
                    </Button>
                  ) : (
                    <Button
                      onClick={handlePauseRecording}
                      className="flex-1 bg-yellow-600 hover:bg-yellow-700"
                    >
                      <Pause className="w-4 h-4 mr-1" />
                      Pausar
                    </Button>
                  )}
                  <Button
                    onClick={handleStopRecording}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Detener
                  </Button>
                </div>
                <Button
                  onClick={handleDiscardRecording}
                  variant="outline"
                  className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Descartar
                </Button>
              </div>
            )}
          </div>

          {/* Tracking Control - Visibility on shared map */}
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Control Seguimiento
            </h3>
            <p className="text-gray-400 text-xs mb-3">
              Activa para ser visible en el mapa compartido donde los organizadores y Rally Masters pueden ver tu ubicación en tiempo real.
            </p>
            <Button
              onClick={toggleTracking}
              className={`w-full h-12 text-base font-semibold ${
                trackingEnabled 
                  ? 'bg-green-600 hover:bg-green-700 animate-pulse' 
                  : 'bg-orange-600 hover:bg-orange-700'
              }`}
              data-testid="toggle-tracking-btn"
            >
              {trackingEnabled ? (
                <>
                  <Radio className="w-5 h-5 mr-2 animate-pulse" />
                  SEGUIMIENTO ACTIVO
                </>
              ) : (
                <>
                  <Radio className="w-5 h-5 mr-2" />
                  ACTIVAR SEGUIMIENTO
                </>
              )}
            </Button>
            {trackingEnabled && (
              <div className="mt-2 p-2 bg-green-900/50 rounded-lg border border-green-500">
                <p className="text-green-300 text-xs flex items-center gap-2">
                  <Radio className="w-3 h-3 animate-pulse" />
                  Tu ubicación se está compartiendo con los organizadores
                </p>
                
                {/* Connection status indicator */}
                <p className={`text-xs mt-1 flex items-center gap-1 ${isOnline ? 'text-green-400' : 'text-orange-400'}`}>
                  {isOnline ? (
                    <>
                      <Wifi className="w-3 h-3" />
                      Conectado
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3 h-3" />
                      Sin conexión - Modo offline activo
                    </>
                  )}
                </p>
                
                {/* Offline queue indicator */}
                {pendingCount > 0 && (
                  <p className="text-orange-300 text-xs mt-1 flex items-center gap-1">
                    <CloudOff className="w-3 h-3" />
                    {pendingCount} ubicaciones en cola
                    {isSyncing && ' (sincronizando...)'}
                  </p>
                )}
                
                {wakeLockRef.current && (
                  <p className="text-blue-300 text-xs mt-1 flex items-center gap-1">
                    🔒 Pantalla bloqueada encendida
                  </p>
                )}
                {lastLocationSent && (
                  <p className="text-green-400 text-xs mt-1">
                    ✓ Última actualización: {lastLocationSent}
                  </p>
                )}
                {locationSendError && !locationSendError.includes('cola') && (
                  <p className="text-red-400 text-xs mt-1">
                    ✗ Error: {locationSendError}
                  </p>
                )}
                {!lastLocationSent && !locationSendError && (
                  <p className="text-yellow-400 text-xs mt-1">
                    ⏳ Esperando posición GPS...
                    {userPosition ? ` (GPS OK: ${userPosition[0].toFixed(4)}, ${userPosition[1].toFixed(4)})` : ' (Sin posición GPS)'}
                  </p>
                )}
                <p className="text-gray-400 text-xs mt-1">
                  ID Inscripción: {registrationId?.substring(0, 8)}...
                </p>
              </div>
            )}
            {!trackingEnabled && gpsEnabled && (
              <div className="mt-2 p-2 bg-yellow-900/30 rounded-lg border border-yellow-600">
                <p className="text-yellow-300 text-xs">
                  ⚠️ GPS activo pero seguimiento NO activado. Pulsa el botón para ser visible.
                </p>
              </div>
            )}
          </div>

          {/* Offline Map Section */}
          {offlineSupported && (
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <CloudOff className="w-4 h-4 text-blue-400" />
                  Mapa Offline
                </h3>
                {/* Map Style Selector */}
                <MapSelector compact className="border-gray-600" />
              </div>
              
              {/* Cache Info */}
              {cacheInfo.count > 0 && (
                <div className="mb-3 p-2 bg-green-900/30 rounded-lg border border-green-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-green-300 flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      {cacheInfo.count} tiles guardados
                    </span>
                    <span className="text-green-400 font-mono">
                      {cacheInfo.formattedSize}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Download Progress */}
              {downloadingMap && (
                <div className="mb-3 space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{downloadCancelled ? 'Cancelando...' : 'Descargando...'}</span>
                    <span>{downloadProgress.cached} / {downloadProgress.total}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(downloadProgress.cached / downloadProgress.total) * 100}%` }}
                    />
                  </div>
                  {/* Stop Download Button */}
                  <Button
                    onClick={() => {
                      cancelDownload();
                      toast.info('Descarga cancelada. Los tiles descargados se han guardado.');
                    }}
                    disabled={downloadCancelled}
                    variant="outline"
                    size="sm"
                    className="w-full border-red-500 text-red-400 hover:bg-red-900/30"
                  >
                    <StopCircle className="w-4 h-4 mr-2" />
                    {downloadCancelled ? 'Cancelando...' : 'Parar Descarga'}
                  </Button>
                </div>
              )}
              
              {/* Error Message */}
              {offlineError && (
                <div className="mb-3 p-2 bg-red-900/30 rounded-lg border border-red-700">
                  <p className="text-red-300 text-xs">{offlineError}</p>
                </div>
              )}
              
              {/* Estimate Info */}
              {eventTrackCoordinates.length > 0 && !downloadingMap && (
                <p className="text-gray-400 text-xs mb-3">
                  {(() => {
                    const estimate = estimateDownloadSize(eventTrackCoordinates);
                    return estimate 
                      ? `~${estimate.tileCount} tiles (~${estimate.estimatedSize})`
                      : 'Calculando...';
                  })()}
                </p>
              )}
              
              {/* Action Buttons */}
              <div className="space-y-2">
                <Button
                  onClick={() => {
                    if (eventTrackCoordinates.length > 0) {
                      downloadTrackArea(eventTrackCoordinates);
                      toast.info('Iniciando descarga del mapa...');
                    } else {
                      toast.error('No hay track cargado para descargar');
                    }
                  }}
                  disabled={downloadingMap || eventTrackCoordinates.length === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600"
                  data-testid="download-offline-map-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingMap ? 'Descargando...' : 'Descargar Mapa del Track'}
                </Button>
                
                {cacheInfo.count > 0 && (
                  <Button
                    onClick={() => {
                      if (window.confirm('¿Eliminar todos los mapas descargados?')) {
                        clearCache();
                        toast.success('Mapas offline eliminados');
                      }
                    }}
                    variant="outline"
                    className="w-full border-red-600 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar Mapas Offline
                  </Button>
                )}
              </div>
              
              <p className="text-gray-500 text-xs mt-3">
                Descarga el mapa del track para usarlo sin conexión a internet.
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="p-4 border-b border-gray-700">
            <h3 className="text-white font-medium mb-3">Leyenda</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-fuchsia-500 rounded"></div>
                <span className="text-gray-300 truncate" title={selectedEventTrack?.name}>
                  {selectedEventTrack?.name || 'Track del evento'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-black rounded border border-gray-600"></div>
                <span className="text-gray-300">Tu recorrido actual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-1 bg-green-500 rounded"></div>
                <span className="text-gray-300">Track guardado seleccionado</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                <span className="text-gray-300">Tu posición</span>
              </div>
            </div>
          </div>

          {/* Saved Tracks */}
          <div className="p-4">
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Save className="w-4 h-4" />
              Tracks Guardados ({savedTracks.length})
            </h3>
            
            {savedTracks.length === 0 ? (
              <p className="text-gray-500 text-sm">No tienes tracks guardados</p>
            ) : (
              <div className="space-y-2">
                {savedTracks.map(track => (
                  <div 
                    key={track.id} 
                    className={`p-3 rounded-lg border ${
                      selectedTrackToView?.id === track.id 
                        ? 'bg-green-900/30 border-green-600' 
                        : 'bg-gray-700 border-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-sm font-medium truncate">
                        {track.name || 'Sin nombre'}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedTrackToView(
                          selectedTrackToView?.id === track.id ? null : track
                        )}
                        className="h-6 px-2 text-gray-300 hover:text-white"
                      >
                        {selectedTrackToView?.id === track.id ? 'Ocultar' : 'Ver'}
                      </Button>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Route className="w-3 h-3" />
                        {track.distance_km?.toFixed(2) || '0'} km
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(track.duration_seconds || 0)}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleExportTrack(track.id)}
                        className="h-7 px-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        GPX
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTrack(track.id)}
                        className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Guardar Track</DialogTitle>
            <DialogDescription>
              Has grabado {distance.toFixed(2)} km en {formatTime(elapsedTime)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="trackName">Nombre del track</Label>
              <Input
                id="trackName"
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
                placeholder={`Track ${new Date().toLocaleDateString('es-ES')}`}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveDialog(false);
                  setRecordedPath([]);
                  setDistance(0);
                  setElapsedTime(0);
                }}
                className="flex-1"
              >
                Descartar
              </Button>
              <Button
                onClick={handleSaveTrack}
                disabled={savingTrack}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {savingTrack ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              ¡Track Guardado!
            </DialogTitle>
            <DialogDescription>
              &quot;{lastSavedTrack?.name}&quot; guardado correctamente. ¿Quieres compartirlo?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Track Summary */}
            <div className="p-4 bg-gray-100 rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-gray-900">{lastSavedTrack?.distance_km?.toFixed(2)}</p>
                  <p className="text-xs text-gray-600">km recorridos</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{formatTime(lastSavedTrack?.duration_seconds || 0)}</p>
                  <p className="text-xs text-gray-600">duración</p>
                </div>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Compartir vía:</p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={shareViaWhatsApp}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
                <Button
                  onClick={shareViaTelegram}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Telegram
                </Button>
                <Button
                  onClick={shareViaEmail}
                  variant="outline"
                  className="border-gray-300"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
              </div>
            </div>

            {/* Export GPX Button */}
            {lastSavedTrack?.id && (
              <Button
                onClick={() => {
                  handleExportTrack(lastSavedTrack.id);
                  toast.success('Descargando GPX...');
                }}
                variant="outline"
                className="w-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar como archivo GPX
              </Button>
            )}

            {/* Close Button */}
            <Button
              onClick={() => setShowShareDialog(false)}
              variant="ghost"
              className="w-full"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
